import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Building2,
  HardHat,
  Users,
  Car,
  MessageSquare,
  Megaphone,
  Bot,
  Brain,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  DollarSign,
  Wallet,
  BarChart3,
  PieChart,
  TrendingUp,
  FileText,
  ClipboardList,
  RefreshCw,
  ArrowLeftRight,
  AlertCircle,
  CheckCircle,
  CreditCard,
  Boxes,
  Scale
} from 'lucide-react';
import { cn } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';

const menuItems = [
  { 
    path: '/', 
    label: 'Dashboard', 
    icon: LayoutDashboard,
    color: '#6366f1'
  },
  { 
    path: '/financeiro', 
    label: 'Financeiro', 
    icon: DollarSign,
    color: '#10b981',
    permission: 'financeiro:read',
    children: [
      { path: '/financeiro', label: 'Dashboard', icon: Wallet, permission: 'financeiro:read' },
      { path: '/financeiro/transacoes', label: 'Transações', icon: ArrowLeftRight, permission: 'financeiro:read' },
      { path: '/financeiro/fluxo-caixa', label: 'Fluxo de Caixa', icon: TrendingUp, permission: 'financeiro:read' },
      { path: '/financeiro/dre', label: 'DRE', icon: BarChart3, permission: 'financeiro:read' },
      { path: '/financeiro/consolidado', label: 'Visão Consolidada', icon: PieChart, permission: 'financeiro:read' },
      { path: '/financeiro/contas-pagar', label: 'Contas a Pagar', icon: AlertCircle, permission: 'financeiro:read' },
      { path: '/financeiro/contas-receber', label: 'Contas a Receber', icon: CheckCircle, permission: 'financeiro:read' },
      { path: '/financeiro/conciliacao', label: 'Conciliação', icon: RefreshCw, permission: 'financeiro:read' },
      { path: '/financeiro/cartoes', label: 'Cartões', icon: CreditCard, permission: 'financeiro:read' },
    ]
  },
  { 
    path: '/imobiliaria', 
    label: 'Imobiliária', 
    icon: Building2,
    color: '#ec4899',
    permission: 'imobiliaria:read'
  },
  { 
    path: '/construtora', 
    label: 'Construtora', 
    icon: HardHat,
    color: '#f59e0b',
    permission: 'construtora:read'
  },
  { 
    path: '/rh', 
    label: 'RH', 
    icon: Users,
    color: '#3b82f6',
    permission: 'rh:read'
  },
  { 
    path: '/frota', 
    label: 'Frota', 
    icon: Car,
    color: '#8b5cf6',
    permission: 'frota:read'
  },
  {
    path: '/estoque',
    label: 'Estoque',
    icon: Boxes,
    color: '#22c55e',
    permission: 'estoque:read'
  },
  {
    path: '/juridico',
    label: 'Jurídico',
    icon: Scale,
    color: '#eab308',
    permission: 'juridico:read'
  },
  {
    path: '/documentos',
    label: 'Documentos/OCR',
    icon: FileText,
    color: '#38bdf8',
    permission: 'documentos:read'
  },
  {
    path: '/revisoes',
    label: 'Revisões',
    icon: ClipboardList,
    color: '#f59e0b',
    permission: 'revisoes:read'
  },
  { 
    path: '/chat', 
    label: 'Chat', 
    icon: MessageSquare,
    color: '#06b6d4',
    permission: 'chat:read',
    badge: 3
  },
  { 
    path: '/marketing', 
    label: 'Marketing', 
    icon: Megaphone,
    color: '#f97316',
    permission: 'marketing:read'
  },
  { 
    path: '/nexusteam', 
    label: 'NexusTeam', 
    icon: Bot,
    color: '#a855f7',
    permission: 'nexus_team:admin'
  },
  { 
    path: '/predictive', 
    label: 'Predictive AI', 
    icon: Brain,
    color: '#14b8a6',
    permission: 'predictive:admin'
  },
];

const bottomItems = [
  { path: '/settings', label: 'Administração', icon: Settings, color: '#64748b', permission: 'users:read' },
];

export const Sidebar = ({ collapsed = false, onToggle, mobile = false, onMobileClose }) => {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState('financeiro');
  const { hasPermission } = useAuth();
  const previousPath = useRef(location.pathname);

  const canShow = (item) => !item.permission || hasPermission(item.permission);
  const visibleMenuItems = menuItems
    .filter(canShow)
    .map((item) => ({
      ...item,
      children: item.children ? item.children.filter(canShow) : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);
  const visibleBottomItems = bottomItems.filter(canShow);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    if (mobile && previousPath.current !== location.pathname) onMobileClose?.();
    previousPath.current = location.pathname;
  }, [location.pathname, mobile, onMobileClose]);

  return (
    <motion.aside
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
      className={cn(
        'fixed left-0 top-0 z-[90] h-full',
        'glass-panel border-r border-white/10',
        'transition-all duration-300 ease-out',
        mobile ? 'w-72 max-w-[88vw] lg:hidden' : (collapsed ? 'hidden w-20 lg:block' : 'hidden w-72 lg:block')
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
        <NavLink to="/" className="flex items-center gap-3 overflow-hidden">
          <img
            src={`${process.env.PUBLIC_URL}/nexus-erp-icon-64.png`}
            alt="Nexus ERP"
            className="flex-shrink-0 w-10 h-10 rounded-xl object-cover ring-1 ring-cyan-200/15 shadow-lg shadow-cyan-950/30"
          />
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden whitespace-nowrap"
              >
                <span className="text-xl font-bold bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
                  Nexus
                </span>
                <span className="text-xs text-white/50 block -mt-1">ERP</span>
              </motion.div>
            )}
          </AnimatePresence>
        </NavLink>
        {mobile && <button type="button" onClick={onMobileClose} className="rounded-lg p-2 text-white/65 hover:bg-white/10 hover:text-white" aria-label="Fechar menu"><X className="h-5 w-5" /></button>}
      </div>

      {/* Navigation */}
      <nav className="flex flex-col h-[calc(100%-5rem)] overflow-y-auto py-4 px-3">
        <div className="flex-1 space-y-1">
          {visibleMenuItems.map((item, index) => (
            <div key={item.path}>
              {item.children && !collapsed ? (
                <div className="mb-2">
                  <button
                    onClick={() => setExpandedMenu(expandedMenu === item.path ? null : item.path)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-300',
                      isActive(item.path) 
                        ? 'text-white bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border border-white/10' 
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: isActive(item.path) ? `${item.color}20` : 'transparent' }}
                      >
                        <item.icon 
                          className="w-5 h-5" 
                          style={{ color: isActive(item.path) ? item.color : undefined }} 
                        />
                      </div>
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <motion.svg
                      animate={{ rotate: expandedMenu === item.path ? 180 : 0 }}
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                    {item.badge && (
                      <span className="absolute right-2 top-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                        {item.badge}
                      </span>
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {expandedMenu === item.path && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden ml-4 mt-1 space-y-1 border-l border-white/10 pl-4"
                      >
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                              location.pathname === child.path
                                ? 'text-white bg-white/10'
                                : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                            )}
                          >
                            <child.icon className="w-4 h-4" />
                            {child.label}
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <SidebarItem
                  item={item}
                  isActive={isActive(item.path)}
                  collapsed={collapsed}
                  index={index}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-white/10 space-y-1">
          {visibleBottomItems.map((item, index) => (
            <SidebarItem
              key={item.path}
              item={item}
              isActive={isActive(item.path)}
              collapsed={collapsed}
              index={index + visibleMenuItems.length}
            />
          ))}
        </div>
      </nav>

      {/* Toggle button */}
      {!mobile && <button
        onClick={onToggle}
        className="absolute -right-3 top-24 w-6 h-6 rounded-full bg-accent-primary text-white flex items-center justify-center shadow-glow hover:scale-110 transition-transform"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </button>}
    </motion.aside>
  );
};

const SidebarItem = ({ item, isActive, collapsed, index }) => {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      className={cn(
        'group relative flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300',
        isActive 
          ? 'text-white' 
          : 'text-white/60 hover:text-white hover:bg-white/5'
      )}
    >
      {/* Active background indicator */}
      {isActive && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute inset-0 rounded-xl bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border border-white/10"
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}

      {/* Glow effect for active item */}
      {isActive && (
        <motion.div
          layoutId="sidebar-glow"
          className="absolute left-0 w-1 h-8 rounded-r-full"
          style={{ backgroundColor: item.color }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.05 }}
        />
      )}

      {/* Icon */}
      <div
        className={cn(
          'relative z-10 flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
          isActive ? 'bg-white/10' : 'group-hover:bg-white/5'
        )}
      >
        <Icon 
          className="w-5 h-5 transition-colors" 
          style={{ color: isActive ? item.color : undefined }}
        />
        {item.badge && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
            {item.badge}
          </span>
        )}
      </div>

      {/* Label */}
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'relative z-10 text-sm font-medium whitespace-nowrap overflow-hidden',
              isActive ? 'text-white' : 'text-white/70'
            )}
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
    </NavLink>
  );
};

export default Sidebar;
